import { useState } from "react";
import "./card.scss";
import { LayoutVariants } from "src/api/types";
import useAuth from "src/hooks/selectors/useAuth";
import Link from "src/components/Link";
import Loader from "src/components/Loader";
import Progress from "src/components/Progress";

type CardProps = {
	type?: string;
	children?: any;
	className?: string;
	contentClassName?: string;
	activeClassName?: string;
	steps?: {
		total: number;
		current: number;
	};
	to?:
		| string
		| {
				modal: string;
		  };
	created_at?: string;
	title?: string;
	style?: any;
	closeable?: boolean;
	id?: string;
	onClick?: (e: any) => any;
	variant?: LayoutVariants;
	ref?: any;
	bg?: string | null;
	target?: string;
	actions?: any;
	loading?: boolean;
};

const Card = ({
	type,
	children,
	steps,
	className,
	to,
	title,
	style,
	closeable,
	contentClassName,
	activeClassName,
	id,
	onClick,
	variant,
	bg,
	ref,
	target,
	actions,
	loading,
}: CardProps) => {
	const classes = ["card rounded-md flex flex-col"];
	const auth = useAuth();
	const [visible, setVisible] = useState(true);

	if (bg) {
		classes.push(`bg-${bg}`);
	}

	if (!bg) {
		classes.push(`bg-white text-dark`);
	}

	if (type) classes.push(`card_${type}`);
	if (steps) classes.push("card_has-steps");
	if (title) classes.push("card_has-title");
	if (closeable) classes.push("card_closeable");
	if (className) classes.push(className);
	let _contentClassName = contentClassName || "";

	if (
		!_contentClassName?.includes(" p-") &&
		!_contentClassName?.includes(" px-") &&
		!_contentClassName?.includes(" py-")
	) {
		_contentClassName += " p-6";
	}

	if (variant === "dark") {
		_contentClassName += ` bg-dark-accent text-white`;
	}

	let _className = classes.join(" ");
	if (auth?.type === "student" && !_className?.includes("bg-")) {
		_className +=
			" dark:bg-dark-accent bg-white dark:text-white transition-all";
	}

	if (_className?.includes("bg-dark")) {
		_className += " text-white";
	}

	if (!_className?.includes("shadow")) {
		_className += " shadow border border-gray";
	}

	const renderChildren = () => {
		return (
			<>
				{steps?.total && <Progress {...steps} />}
				<div
					className={`card-content flex flex-col flex-1 rounded-md ${
						_contentClassName || ""
					}`}
				>
					{title && (
						<div
							className={`flex items-center justify-between ${
								visible && "mb-5"
							}`}
						>
							<p className="mb-0 text-[22px]">
								<strong>{title}</strong>
							</p>
							{actions}
							{closeable && (
								<span
									className="cursor-pointer"
									onClick={() => setVisible(!visible)}
								>
									{visible ? (
										<i className="fas fa-angle-up"></i>
									) : (
										<i className="fas fa-angle-down"></i>
									)}
								</span>
							)}
						</div>
					)}
					{loading ? (
						<div className="flex flex-1 flex-col items-center justify-center">
							<Loader />
						</div>
					) : (
						visible && children
					)}
				</div>
			</>
		);
	};

	if (to) {
		return (
			<Link
				{...{ to, ref, id, onClick, target, activeClassName }}
				className={_className}
			>
				{renderChildren()}
			</Link>
		);
	}

	return (
		<div {...{ style, ref, id, onClick }} className={_className}>
			{renderChildren()}
		</div>
	);
};

interface CardHeadProps {
	title?: string;
	children?: any;
}

export const CardHead = ({ title, children }: CardHeadProps) => (
	<div className="card-head">
		{title && <h2>{title}</h2>}
		{children}
	</div>
);

Card.Head = CardHead;

export default Card;
